import { useLocation, useRouter } from "@tanstack/react-router";
import {
  BarChart2,
  Clock,
  FileText,
  History,
  Home,
  Layers,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Tag,
  User,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { signOut, useSession } from "@/lib/auth-client";

interface SidebarProps {
  flaggedCount?: number;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export function Sidebar({ flaggedCount = 0, collapsed, onToggleCollapsed }: SidebarProps) {
  const location = useLocation();
  const router = useRouter();
  const { data: session } = useSession();
  const userRole = session?.user?.role ?? "";
  const isAdmin = userRole === "ops";
  const isMgrOrAdmin = userRole === "ops" || userRole === "manager";

  function initials(name: string | undefined | null) {
    if (!name) return "U";
    return name
      .split(" ")
      .slice(0, 2)
      .map((p) => p[0])
      .join("")
      .toUpperCase();
  }

  async function handleSignOut() {
    await signOut();
    router.navigate({ to: "/login" });
  }

  function handleEditProfile() {
    router.navigate({ to: "/dashboard/profile" });
  }

  const engineeringItems = [
    { id: "dashboard", label: "Dashboard", icon: Home, href: "/dashboard" },
    { id: "log-time", label: "Log Time", icon: Clock, href: "/dashboard/log-time" },
    { id: "history", label: "History", icon: Layers, href: "/dashboard/history" },
  ];

  const adminItems = [
    { id: "work-orders", label: "Work Orders", icon: FileText, href: "/dashboard/work-orders" },
    { id: "reports", label: "Reports", icon: BarChart2, href: "/dashboard/reports" },
    { id: "settings", label: "Settings", icon: Settings, href: "/dashboard/settings" },
  ];

  const mgrOrAdminItems = [
    {
      id: "config-history",
      label: "Configuration History",
      icon: History,
      href: "/dashboard/configuration-history",
    },
    {
      id: "overhead-categories",
      label: "Overhead Categories",
      icon: Tag,
      href: "/dashboard/overhead-categories",
    },
  ];

  function isActive(href: string) {
    if (href === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(href);
  }

  function navigate(href: string) {
    router.navigate({ to: href });
  }

  function roleLabel(role: string) {
    if (role === "ops") return "Operations Admin";
    if (role === "manager") return "Eng. Manager";
    if (role === "executive") return "Executive";
    if (role === "finance") return "Finance Lead";
    if (role === "new_engineer") return "New Engineer";
    return "Field Engineer";
  }

  const shortcutHint =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)
      ? "⌘B"
      : "Ctrl+B";

  return (
    <aside className="sidebar">
      {/* Brand */}
      <div className="sidebar__brand">
        <div className="sidebar__brand-text">
          <div className="sidebar__brand-title">Urban Solutions Group</div>
          <div className="sidebar__brand-sub">Time Tracker</div>
        </div>
        <button
          type="button"
          className="sidebar__collapse-toggle"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={`${collapsed ? "Expand" : "Collapse"} sidebar (${shortcutHint})`}
        >
          {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
      </div>

      {/* Engineering nav */}
      <div className="sidebar__section-label">Engineering</div>
      <nav className="sidebar__nav">
        {engineeringItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`nav-item${isActive(item.href) ? " is-active" : ""}`}
            onClick={() => navigate(item.href)}
            title={item.label}
          >
            <item.icon size={18} className="nav-item__icon" />
            <span className="nav-item__label">{item.label}</span>
            {item.id === "dashboard" && flaggedCount > 0 && (
              <span className="nav-item__count nav-item__count--alert">{flaggedCount}</span>
            )}
          </button>
        ))}
      </nav>

      {/* Admin nav */}
      {isAdmin && (
        <>
          <div className="sidebar__section-label">Admin</div>
          <nav className="sidebar__nav">
            {adminItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`nav-item${isActive(item.href) ? " is-active" : ""}`}
                onClick={() => navigate(item.href)}
                title={item.label}
              >
                <item.icon size={18} className="nav-item__icon" />
                <span className="nav-item__label">{item.label}</span>
              </button>
            ))}
          </nav>
        </>
      )}

      {/* Mgr / Admin shared nav */}
      {isMgrOrAdmin && (
        <>
          <div className="sidebar__section-label">Operations</div>
          <nav className="sidebar__nav">
            {mgrOrAdminItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`nav-item${isActive(item.href) ? " is-active" : ""}`}
                onClick={() => navigate(item.href)}
                title={item.label}
              >
                <item.icon size={18} className="nav-item__icon" />
                <span className="nav-item__label">{item.label}</span>
              </button>
            ))}
          </nav>
        </>
      )}

      {/* Footer / profile menu */}
      <div className="sidebar__footer">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="sidebar__profile"
              title={session?.user?.name ?? "Account"}
            >
              <span className="sidebar__avatar" aria-hidden="true">
                {initials(session?.user?.name)}
              </span>
              <span className="sidebar__user">
                <span className="sidebar__user-name">{session?.user?.name ?? "User"}</span>
                <span className="sidebar__user-role">{roleLabel(userRole)}</span>
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56">
            <DropdownMenuItem onSelect={handleEditProfile}>
              <User size={14} />
              Edit profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleSignOut}>
              <LogOut size={14} />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
